import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Download } from 'lucide-react';
import './EventPhotos.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://live-event-photos.onrender.com/api';

// Helper function to generate thumbnail URL
// For B2 photos, use thumbnail_url if available, otherwise fall back to full URL
// Cloudinary URLs get transformation parameters
const getCloudinaryThumbnail = (url, width = 400, height = 400, thumbnailUrl = null) => {
  if (!url) return url;
  
  // B2 URLs - use thumbnail_url if available (pre-generated thumbnail)
  if (url.includes('backblazeb2.com')) {
    return thumbnailUrl || url; // Use thumbnail if available, otherwise full URL
  }
  
  // Cloudinary URLs - add transformation parameters
  if (url.includes('cloudinary.com')) {
    return url.replace(
      '/upload/',
      `/upload/w_${width},h_${height},c_fill,q_auto,f_auto/`
    );
  }
  
  return url;
};

function EventGallery() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePhotoId, setActivePhotoId] = useState(null);
  const [hoveredPhotoId, setHoveredPhotoId] = useState(null);
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [showMusicModal, setShowMusicModal] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [audioElement, setAudioElement] = useState(null);

  // Music options (royalty-free instrumental tracks)
  const musicOptions = [
    { id: 'none', name: 'No Music', url: null },
    { id: 'upbeat', name: 'Upbeat Pop', url: 'https://www.bensound.com/bensound-music/bensound-ukulele.mp3' },
    { id: 'chill', name: 'Chill Electronic', url: 'https://www.bensound.com/bensound-music/bensound-creativeminds.mp3' },
    { id: 'acoustic', name: 'Acoustic Folk', url: 'https://www.bensound.com/bensound-music/bensound-sunny.mp3' },
    { id: 'cinematic', name: 'Cinematic', url: 'https://www.bensound.com/bensound-music/bensound-epic.mp3' },
    { id: 'jazz', name: 'Smooth Jazz', url: 'https://www.bensound.com/bensound-music/bensound-jazzyfrenchy.mp3' }
  ];

  // Always use dark mode logo for gallery
  const logoUrl = 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  // Toggle overlay on mobile tap
  const handlePhotoTap = (photoId) => {
    setActivePhotoId(activePhotoId === photoId ? null : photoId);
  };

  // Save/share photo (uses Web Share API on mobile, downloads on desktop)
  const handleDownload = async (photoUrl, photoName) => {
    try {
      // Fetch image as blob
      const response = await fetch(photoUrl);
      const blob = await response.blob();
      
      // Try Web Share API first (mobile)
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], photoName || 'photo.jpg', { type: blob.type });
        
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Photo from Event',
            text: 'Check out this photo!'
          });
          return;
        }
      }
      
      // Fallback: traditional download (desktop)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = photoName || 'photo.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download/share failed:', error);
      // Final fallback: open in new tab
      window.open(photoUrl, '_blank');
    }
  };

  // Slideshow auto-advance
  useEffect(() => {
    if (!slideshowActive || photos.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % photos.length);
    }, 3000); // 3 seconds per photo

    return () => clearInterval(interval);
  }, [slideshowActive, photos.length]);

  // Keyboard navigation for slideshow
  useEffect(() => {
    if (!slideshowActive) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        setSlideshowActive(false);
        // Stop music when exiting
        if (audioElement) {
          audioElement.pause();
          audioElement.currentTime = 0;
        }
      } else if (e.key === 'ArrowRight') {
        setCurrentSlideIndex((prev) => (prev + 1) % photos.length);
      } else if (e.key === 'ArrowLeft') {
        setCurrentSlideIndex((prev) => (prev - 1 + photos.length) % photos.length);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [slideshowActive, photos.length]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, [audioElement]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        // Fetch event details and all photos
        const [eventResponse, photosResponse] = await Promise.all([
          axios.get(`${API_URL}/events/${eventId}`),
          axios.get(`${API_URL}/events/${eventId}/photos`)
        ]);
        
        setEvent(eventResponse.data);
        setPhotos(photosResponse.data.photos || []);
      } catch (err) {
        console.error('Failed to load gallery:', err);
        setError(err.response?.data?.error || 'Failed to load gallery');
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [eventId]);

  if (loading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: '#0f172a', color: '#fff' }}>
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading event gallery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#0f172a' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <h2 style={{ color: '#EF4444', marginBottom: '16px' }}>❌ Gallery Not Found</h2>
          <p style={{ color: '#94a3b8' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-gallery-container" style={{ backgroundColor: '#0f172a', minHeight: '100vh', paddingBottom: '40px' }}>
      {/* Header */}
      <div className="public-gallery-header" style={{
        padding: '20px',
        textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        marginBottom: '20px'
      }}>
        <img src={logoUrl} alt="EventPic Live" style={{ height: '40px', marginBottom: '16px' }} />
        <h1 style={{ color: '#fff', fontSize: '24px', marginBottom: '8px' }}>{event?.name || 'Event Gallery'}</h1>
        <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '16px' }}>{photos.length} {photos.length === 1 ? 'photo' : 'photos'}</p>
        {photos.length > 0 && (
          <button
            onClick={() => setShowMusicModal(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3B82F6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ▶️ Start Slideshow
          </button>
        )}
      </div>

      {/* Photos Grid */}
      <div className="public-gallery-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '16px',
        padding: '0 20px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {photos.map(photo => (
          <div
            key={photo.id}
            className="public-gallery-photo"
            style={{
              position: 'relative',
              aspectRatio: '1',
              borderRadius: '8px',
              overflow: 'hidden',
              cursor: 'pointer',
              backgroundColor: '#1e293b'
            }}
            onClick={() => handlePhotoTap(photo.id)}
            onMouseEnter={() => setHoveredPhotoId(photo.id)}
            onMouseLeave={() => setHoveredPhotoId(null)}
          >
            <img
              src={getCloudinaryThumbnail(photo.url, 400, 400, photo.thumbnail_url)}
              alt="Event photo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            
            {/* Hover overlay (desktop) */}
            {hoveredPhotoId === photo.id && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  transition: 'opacity 0.2s'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDownload(photo.url, `event-photo-${photo.id}.jpg`);
                }}
              >
                <button
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#3B82F6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    pointerEvents: 'none'
                  }}
                >
                  <Download size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
                  Download
                </button>
              </div>
            )}
            
            {/* Tap overlay (mobile) */}
            {activePhotoId === photo.id && (
              <div
                className="photo-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 20
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDownload(photo.url, `event-photo-${photo.id}.jpg`);
                  }}
                  style={{
                    padding: '16px 32px',
                    backgroundColor: '#3B82F6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
                  }}
                >
                  <Download size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
                  Download
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {photos.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: '#94a3b8', fontSize: '16px' }}>No photos yet</p>
        </div>
      )}

      {/* Music Selection Modal */}
      {showMusicModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowMusicModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1e293b',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: '#fff', fontSize: '24px', marginBottom: '24px', textAlign: 'center' }}>Choose Slideshow Music</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {musicOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => {
                    setSelectedMusic(option);
                    setShowMusicModal(false);
                    setSlideshowActive(true);
                    setCurrentSlideIndex(0);
                    
                    // Start playing music if selected
                    if (option.url) {
                      const audio = new Audio(option.url);
                      audio.loop = true;
                      audio.play();
                      setAudioElement(audio);
                    }
                  }}
                  style={{
                    padding: '16px',
                    backgroundColor: selectedMusic?.id === option.id ? '#3B82F6' : '#334155',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMusic?.id !== option.id) {
                      e.target.style.backgroundColor = '#475569';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedMusic?.id !== option.id) {
                      e.target.style.backgroundColor = '#334155';
                    }
                  }}
                >
                  {option.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowMusicModal(false)}
              style={{
                marginTop: '24px',
                padding: '12px',
                backgroundColor: 'transparent',
                color: '#94a3b8',
                border: '1px solid #475569',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Slideshow Modal */}
      {slideshowActive && photos.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#000',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => {
            setSlideshowActive(false);
            if (audioElement) {
              audioElement.pause();
              audioElement.currentTime = 0;
            }
          }}
        >
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSlideshowActive(false);
              if (audioElement) {
                audioElement.pause();
                audioElement.currentTime = 0;
              }
            }}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '24px',
              cursor: 'pointer',
              zIndex: 10000
            }}
          >
            ✕
          </button>

          {/* Current photo with Ken Burns effect and fade */}
          <img
            key={currentSlideIndex}
            src={photos[currentSlideIndex]?.url}
            alt="Slideshow"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              animation: 'kenBurnsFade 5s ease-out',
              transformOrigin: 'center center'
            }}
            onClick={(e) => e.stopPropagation()}
          />
          
          <style>{`
            @keyframes kenBurnsFade {
              0% {
                opacity: 0;
                transform: scale(1);
              }
              5% {
                opacity: 1;
              }
              100% {
                opacity: 1;
                transform: scale(1.15);
              }
            }
          `}</style>

          {/* Navigation arrows */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentSlideIndex((prev) => (prev - 1 + photos.length) % photos.length);
            }}
            style={{
              position: 'absolute',
              left: '20px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              fontSize: '24px',
              cursor: 'pointer'
            }}
          >
            ‹
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentSlideIndex((prev) => (prev + 1) % photos.length);
            }}
            style={{
              position: 'absolute',
              right: '20px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              fontSize: '24px',
              cursor: 'pointer'
            }}
          >
            ›
          </button>

          {/* Progress indicator */}
          <div
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px'
            }}
          >
            {currentSlideIndex + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default EventGallery;
